<template>
  <div class="details">
    <div class="details__box">
      <ui-input
          :type="'text'"
          :label="t('fields.firstName')"
          :rules="[]"
          v-model="firstName"
      />
      <ui-input
          :type="'text'"
          :label="t('fields.lastName')"
          :rules="[]"
          v-model="lastName"
      />
    </div>
    <ui-input
        :type="'email'"
        :label="t('fields.email')"
        :rules="[isEmail]"
        v-model="email"
    />
    <div class="details__block">
      <p class="details__block-title">{{ t('profile.details.title') }}</p>
      <p>{{ t('profile.details.leave') }}</p>
      <ui-input
          :type="'password'"
          :label="t('fields.newPassword')"
          :rules="[isPasswordValid]"
          v-model="newPassword"
      />
      <ui-input
          :type="'password'"
          :label="t('fields.confirmNewPassword')"
          :rules="[compareStrings]"
          v-model="confirmNewPassword"
      />
    </div>
    <button class="details__button" @click="updateInfo">{{ t('buttons.saveChanges') }}</button>
  </div>
</template>

<script setup>
import UiInput from "@/components/ui/ui-input.vue";
import {useI18n} from "vue-i18n";
import {computed, ref, watch} from "vue";
import {isEmail, isPasswordValid} from "@/core/rules/textFieldRules.js";
import {useAuthStore} from "@/stores/auth.js";
import {useRouter} from "vue-router";
import translation from "@/core/helpers/translations.js";
import {ElNotification} from "element-plus";
import {Auth} from "@/core/api/auth.js";

const {t} = useI18n()
const router = useRouter()
const authStore = useAuthStore()

const accessToken = computed(() => authStore.accessToken)
const user = computed(() => authStore.user)
const userUuid = computed(() => {
  return authStore.user ? authStore.user.uuid : ''
})
const userEmail = computed(() => {
  return authStore.user ? authStore.user?.email : ''
})

const firstName = ref(user.value ? user.value.firstName : '')
const lastName = ref(user.value ? user.value.lastName : '')
const email = ref(user.value ? user.value.email : '')
const newPassword = ref('')
const confirmNewPassword = ref('')

const compareStrings = (v) => {
  if (v === newPassword.value) return true
  return t('errors.compare')
}

watch(
  () => user.value,
  (val) => {
    if (val) {
      firstName.value = user.value.firstName
      lastName.value = user.value.lastName
      email.value = user.value.email
    }
  },
  { deep: true, immediate: true }
)

const logout = async () => {
  authStore.setUser({
    user: null,
    accessToken: null
  })

  localStorage.removeItem('ngs-refresh')
  localStorage.removeItem('ngs-remember')
  localStorage.removeItem('ngs-login')

  await router.push({
    name: 'home',
    params: {
      locale: localStorage.getItem('ngs-user-locale') || 'en-gb'
    }
  })
}

const updateInfo = async () => {
  if (!user.value) return

  const res = await Auth.updateUser(
    `uuid: "${userUuid.value}"` +
      (email.value ? `, email: "${email.value}"` : "") +
      (firstName.value ? `, firstName: "${firstName.value}"` : "") +
      (lastName.value ? `, lastName: "${lastName.value}"` : ""),
      accessToken.value
  )

  if (res.data.updateUser) {
    ElNotification({
      message: t("popup.successUpdate"),
      type: "success"
    });

    if (userEmail.value !== email.value) {
      await logout()

      ElNotification({
        message: t("popup.confirmEmail"),
        type: "success"
      });

      authStore.setUser({
        user: res.data.updateUser.user,
        accessToken: accessToken.value
      })

      if (res.data.updateUser.user.language !== translation.currentLocale) {
        translation.switchLanguage(res.data.updateUser.user.language)
      }
    }

    authStore.setUser({
      user: res.data.updateUser.user,
      accessToken: accessToken.value
    })
  } else {
    ElNotification({
      title: t('popup.error'),
      message: res.errors[0].message,
      type: 'error'
    })
  }
}
</script>

<style lang="scss" scoped>
.details {
  display: flex;
  flex-direction: column;
  gap: 35px;

  &__box {
    display: flex;
    align-items: center;
    gap: 50px;
  }

  &__block {
    position: relative;
    border: 2px solid #E8E8E8;
    border-radius: 5px;
    padding: 35px 40px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    &-title {
      position: absolute;
      top: -17px;
      left: 50px;
      padding-inline: 10px;
      background-color: $white;

      color: #5A5A5A;
      font-size: 21px;
      font-weight: 700;
      line-height: 28.16px;
      letter-spacing: 0.42px;
    }
  }

  &__button {
    width: fit-content;
    cursor: pointer;
    border-radius: 5px;
    background-color: #83B735;
    padding: 7px 15px;
    transition: 0.2s;

    color: $white;
    font-size: 15px;
    font-weight: 400;
    line-height: 28.16px;
    letter-spacing: -0.3px;
    
    @include hover {
      background-color: #638c25;
    }
  }
}

@media (max-width: 750px) {
  .details {
    gap: 20px;

    &__box {
      flex-direction: column;
      gap: 20px;
    }

    &__block {
      padding: 15px 20px;
    }
  }
}

@media (max-width: 550px) {
  .details {
    &__block {
      &-title {
        left: 20px;
        top: -15px;
        font-size: 14px;
      }
    }
  }
}
</style>